import React, {useLayoutEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectTutorialState} from '../../../redux/reducers/privateStateReducer/selectors';
import {selectCurrentPlayer} from '../../../redux/reducers/sharedStateReducer/selectors';
import {getTutorialChapter} from '../../../configs/tutorial';
import {BARBA_AVATAR_SRC} from '../BarbaReplicaModal/BarbaReplicaModal';
import './TutorialTasksPanel.css';

// Зазор между плашкой задания и строкой игроков (плашкой ходящего)
const PANEL_GAP_PX = 8;
// Ширина плашки ходящего соперника, пока ее нет на экране: она висит по
// центру под кружком игрока и шириной по содержимому
// (HeaderActingPlayerBadge.css) — с двумя политиками около 92 px
const ACTING_BADGE_FALLBACK_WIDTH_PX = 92;
const PLACEMENT_RETRY_MS = 300;

/**
 * Где поставить плашку задания, чтобы она не закрывала строку игроков и
 * выпадающую под ходящим плашку с его политикой и ресурсами:
 *  - на телефоне строка игроков — полоса под шапкой у правого края: плашка
 *    встает под шапкой между левым краем (ярлыки рынка) и этой полосой;
 *  - на десктопе строка в самой шапке справа: плашка под шапкой, правым
 *    краем левее строки и плашки ходящего, которая выступает за левый
 *    кружок на полширины.
 * Строка в обучении всегда узкая (два игрока), но ее ширина — замер, а не число
 * @returns {{isStrip: boolean, right: number}|null} right — отступ справа в px
 */
const measurePanelPlacement = () => {
  const strip = document.querySelector('.header-players-strip');
  if (strip) {
    return {isStrip: true, right: window.innerWidth - strip.getBoundingClientRect().left + PANEL_GAP_PX};
  }

  const row = document.querySelector('.header-players-row');
  const firstPlayer = row?.querySelector('[data-player-id]');
  if (!row || !firstPlayer) {
    return null;
  }
  const badgeWidth = document.querySelector('.header-acting-badge')?.offsetWidth || ACTING_BADGE_FALLBACK_WIDTH_PX;
  const playerRect = firstPlayer.getBoundingClientRect();
  const badgeLeft = playerRect.left + playerRect.width / 2 - badgeWidth / 2;
  const occupiedLeft = Math.min(row.getBoundingClientRect().left, badgeLeft);
  return {isStrip: false, right: window.innerWidth - occupiedLeft + PANEL_GAP_PX};
};

const usePanelPlacement = () => {
  const [placement, setPlacement] = useState(null);

  useLayoutEffect(() => {
    let retryTimeoutId = null;
    const update = () => {
      const next = measurePanelPlacement();
      setPlacement((previous) => (
        previous && next && previous.isStrip === next.isStrip && previous.right === next.right ? previous : next
      ));
      // Шапки с игроками еще нет (поле грузится) — до тех пор место по
      // умолчанию, у правого края; замер повторяется, пока она не появится
      clearTimeout(retryTimeoutId);
      if (!next) {
        retryTimeoutId = setTimeout(update, PLACEMENT_RETRY_MS);
      }
    };
    update();

    // Строка игроков меняет ширину и место при смене раскладки (полоса на
    // телефоне, шапка на десктопе) и загрузке портретов
    const header = document.querySelector('.app-header');
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer && header) {
      observer.observe(header);
    }
    window.addEventListener('resize', update);
    return () => {
      clearTimeout(retryTimeoutId);
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return placement;
};

/**
 * Панель текущего задания обучения. История выполненных задач временно
 * скрыта (решим позже, возвращать ли). Плавающая плашка под шапкой на любой
 * ширине: боковые панели на десктопе больше не закреплены, и в списке игроков
 * задание было бы видно только при открытой панели. Место — рядом со строкой
 * игроков, но не на ней (usePanelPlacement). Медальон Барбы слева: задания
 * дает наставник, как советник в пошаговых стратегиях.
 */
const TutorialTasksPanel = ({variant = 'floating'}) => {
  const tutorialState = useSelector(selectTutorialState);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const placement = usePanelPlacement();

  const chapter = getTutorialChapter(tutorialState.chapterId);
  if (!chapter) {
    return null;
  }

  const activeStep = chapter.steps[tutorialState.stepIndex] || null;
  // Ход бота: задания у игрока нет — ждем (стрелка на кнопке завершения с
  // песочными часами это же и показывает). Исключение — шаги, где игрок
  // действует в ход противника (task.duringOpponentTurn: подтвердить бой и
  // т.п.): их текст остается
  const isOpponentTurn = Boolean(currentPlayer?.isBot);
  let activeTask;
  if (tutorialState.isChapterCompleted) {
    activeTask = 'Глава пройдена!';
  } else if (isOpponentTurn && !activeStep?.task?.duringOpponentTurn) {
    activeTask = 'Дождись окончания хода противника';
  } else {
    // Шаг-модалка задачи не несет — панель зовет прочитать подсказку
    activeTask = activeStep?.task?.text || 'Изучи подсказку на экране';
  }

  return (
    <div
      className={`tutorial-tasks-panel tutorial-tasks-panel_${variant}${
        placement?.isStrip ? ' tutorial-tasks-panel_beside-strip' : ''
      }`}
      style={placement ? {'--tutorial-panel-right': `${placement.right}px`} : undefined}
    >
      <div className={'tutorial-tasks-panel_card'}>
        <div className={'tutorial-tasks-panel_avatar'} aria-hidden={'true'}>
          <img className={'tutorial-tasks-panel_avatar-image'} src={BARBA_AVATAR_SRC} alt={''}/>
        </div>
        <div className={'tutorial-tasks-panel_task tutorial-tasks-panel_task--active'}>
          <span>{activeTask}</span>
        </div>
      </div>
    </div>
  );
};

export default TutorialTasksPanel;
